import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, Tag, ArrowRight, Heart, Camera, Upload, Loader2, MapPin, User, X, CheckCircle, BookOpen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import SEO from "../components/SEO";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [towns, setTowns] = useState([]);
  const [activeView, setActiveView] = useState("news"); // "news" or "community"

  // Share a Moment form
  const [showShareForm, setShowShareForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [shareForm, setShareForm] = useState({ name: "", town: "", caption: "", media_url: "" });
  const fileInputRef = useRef(null);

  useEffect(() => { fetchAll(); }, [selectedCategory]);

  const fetchAll = async () => {
    try {
      const [postsRes, catRes, communityRes, townsRes] = await Promise.all([
        axios.get(`${API}/blog/posts`, { params: selectedCategory ? { category: selectedCategory } : {} }),
        axios.get(`${API}/blog/categories`),
        axios.get(`${API}/blog/community-posts`),
        axios.get(`${API}/categories`)
      ]);
      setPosts(postsRes.data);
      setCategories(catRes.data.categories);
      setCommunityPosts(communityRes.data);
      setTowns(townsRes.data.towns || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const getCategoryStyle = (catId) => {
    const styles = {
      sponsor: "bg-[#D4A017] text-[#1A2F23]",
      premium: "bg-[#2C4A3B] text-white",
      news: "bg-[#3B82F6] text-white",
      community: "bg-[#14B8A6] text-white",
      celebration: "bg-[#E87040] text-white",
    };
    return styles[catId] || "bg-[#5C5A52] text-white";
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large. Max 10MB."); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/media/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setShareForm(p => ({ ...p, media_url: res.data.url }));
      toast.success("Photo uploaded!");
    } catch (e) { toast.error("Upload failed. Try again."); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleShareSubmit = async (e) => {
    e.preventDefault();
    if (!shareForm.caption.trim()) { toast.error("Please add a caption"); return; }
    if (!shareForm.media_url) { toast.error("Please upload a photo or video"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/blog/community-submit`, shareForm);
      setSubmitted(true);
      setShareForm({ name: "", town: "", caption: "", media_url: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO
        title="News & Community"
        description="Valley news, sponsor promotions, premium services, and community stories from the East Kootenay, BC."
        keywords="East Kootenay news, Invermere updates, local events BC"
      />
      <NavbarLight />

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1915]">News & Community</h1>
            <p className="text-[#5C5A52] text-lg mt-2">Stories, celebrations, and updates from across the East Kootenay.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href="https://substack.com/@ekvalleys?invite" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-[#2C4A3B] text-[#2C4A3B] hover:bg-[#2C4A3B] hover:text-white" data-testid="blog-substack-btn">
                <BookOpen className="w-4 h-4 mr-2" /> Follow on Substack
              </Button>
            </a>
            <Link to="/donate">
              <Button variant="outline" className="border-[#D4A017] text-[#D4A017] hover:bg-[#D4A017] hover:text-white" data-testid="blog-support-btn">
                <Heart className="w-4 h-4 mr-2" /> Support Us
              </Button>
            </Link>
            <Button
              onClick={() => { setShowShareForm(true); setSubmitted(false); }}
              className="bg-[#E87040] hover:bg-[#D45F30] text-white"
              data-testid="share-moment-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Share a Moment
            </Button>
          </div>
        </div>

        {/* News / Community Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveView("news")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === "news" ? "bg-[#2C4A3B] text-white" : "bg-[#D1CFC5] text-[#5C5A52] hover:bg-[#C1BFB5]"}`}
            data-testid="view-news"
          >
            News & Updates
          </button>
          <button
            onClick={() => setActiveView("community")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === "community" ? "bg-[#E87040] text-white" : "bg-[#D1CFC5] text-[#5C5A52] hover:bg-[#C1BFB5]"}`}
            data-testid="view-community"
          >
            Community Moments
          </button>
        </div>

        {/* News View */}
        {activeView === "news" && (
          <>
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!selectedCategory ? "bg-[#2C4A3B] text-white" : "bg-[#D1CFC5] text-[#5C5A52] hover:bg-[#C1BFB5]"}`}
                data-testid="cat-all"
              >
                All Posts
              </button>
              {categories.filter(c => c.id !== "celebration").map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === cat.id ? getCategoryStyle(cat.id) : "bg-[#D1CFC5] text-[#5C5A52] hover:bg-[#C1BFB5]"}`}
                  data-testid={`cat-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Blog Posts */}
            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#2C4A3B]" />
              </div>
            ) : posts.length > 0 ? (
              <div className="space-y-6">
                {posts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="block group">
                    <article className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 hover:border-[#2C4A3B] transition-colors" data-testid={`blog-post-${post.id}`}>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className={`${getCategoryStyle(post.category)} px-3 py-0.5 rounded-full text-xs font-bold`}>
                          {categories.find(c => c.id === post.category)?.name || post.category}
                        </span>
                        {post.is_featured && (
                          <span className="bg-[#D4A017] text-white px-3 py-0.5 rounded-full text-xs font-bold">Featured</span>
                        )}
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#1A1915] group-hover:text-[#2C4A3B] transition-colors">{post.title}</h2>
                          <p className="text-[#5C5A52] mt-2 line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-[#5C5A52]">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                            <span>By {post.author}</span>
                          </div>
                        </div>
                        {post.image_url && (
                          <img src={post.image_url} alt="" className="w-24 h-24 object-cover rounded-lg hidden md:block" />
                        )}
                      </div>
                      <div className="flex justify-end mt-3">
                        <span className="text-[#2C4A3B] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read more <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-[#F7F6F2] rounded-xl border-2 border-[#D1CFC5]">
                <Tag className="w-12 h-12 mx-auto text-[#D1CFC5] mb-4" />
                <p className="text-[#5C5A52] text-lg mb-2">No posts yet in this category.</p>
                <p className="text-[#5C5A52]/60">Check back soon for updates from the East Kootenay.</p>
              </div>
            )}
          </>
        )}

        {/* Community Moments Gallery */}
        {activeView === "community" && (
          <div>
            {communityPosts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {communityPosts.map((post) => (
                  <div key={post.id} className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl overflow-hidden" data-testid={`community-post-${post.id}`}>
                    {post.media_url && (
                      <img src={post.media_url} alt={post.caption} className="w-full h-56 object-cover" />
                    )}
                    <div className="p-4">
                      <p className="text-[#1A1915] font-medium">{post.caption}</p>
                      <div className="flex items-center gap-3 mt-3 text-sm text-[#5C5A52]">
                        <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{post.name}</span>
                        {post.town && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{post.town}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-[#F7F6F2] rounded-xl border-2 border-[#D1CFC5]">
                <Camera className="w-12 h-12 mx-auto text-[#D1CFC5] mb-4" />
                <p className="text-[#5C5A52] text-lg mb-2">No community moments yet.</p>
                <p className="text-[#5C5A52]/60 mb-4">Be the first to share something from your town!</p>
                <Button onClick={() => { setShowShareForm(true); setSubmitted(false); }}
                  className="bg-[#E87040] hover:bg-[#D45F30] text-white" data-testid="share-moment-empty-btn">
                  <Camera className="w-4 h-4 mr-2" /> Share a Moment
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Share a Moment Modal */}
      {showShareForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowShareForm(false)}>
          <div className="bg-[#EFEDE5] rounded-2xl max-w-lg w-full p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="share-moment-modal">
            <button onClick={() => setShowShareForm(false)} className="absolute top-4 right-4 text-[#5C5A52] hover:text-[#1A1915]">
              <X className="w-5 h-5" />
            </button>

            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-[#14B8A6] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-[#1A1915] mb-2">Thanks for sharing!</h3>
                <p className="text-[#5C5A52]">Your moment will appear after a quick review.</p>
                <Button onClick={() => setShowShareForm(false)} className="mt-6 bg-[#2C4A3B] text-white">Close</Button>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-2xl font-bold text-[#1A1915] mb-1">Share a Moment</h3>
                <p className="text-[#5C5A52] text-sm mb-5">Celebrate what makes our communities special.</p>

                <form onSubmit={handleShareSubmit} className="space-y-4">
                  {/* Photo Upload */}
                  <div>
                    <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={handleFileUpload} className="hidden" data-testid="community-file-input" />
                    {shareForm.media_url ? (
                      <div className="relative">
                        <img src={shareForm.media_url} alt="Preview" className="w-full h-48 object-cover rounded-xl border-2 border-[#D1CFC5]" />
                        <button type="button" onClick={() => setShareForm(p => ({ ...p, media_url: "" }))}
                          className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-red-50">
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full h-40 border-2 border-dashed border-[#D1CFC5] rounded-xl flex flex-col items-center justify-center gap-2 hover:border-[#E87040] transition-colors bg-[#F7F6F2]"
                        data-testid="community-upload-btn">
                        {uploading ? (
                          <><Loader2 className="w-8 h-8 text-[#E87040] animate-spin" /><span className="text-sm text-[#5C5A52]">Uploading...</span></>
                        ) : (
                          <><Upload className="w-8 h-8 text-[#5C5A52]" /><span className="text-sm text-[#5C5A52]">Tap to upload a photo or video</span><span className="text-xs text-[#5C5A52]/60">Max 10MB</span></>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Caption */}
                  <div>
                    <Label className="text-[#1A1915]">What's the moment? *</Label>
                    <Textarea
                      value={shareForm.caption} onChange={(e) => setShareForm(p => ({ ...p, caption: e.target.value }))}
                      className="mt-1 bg-white border-[#D1CFC5]" rows={3}
                      placeholder="First snowfall in Fernie... sunrise over Lake Windermere... community BBQ in Golden..."
                      data-testid="community-caption"
                    />
                  </div>

                  {/* Name & Town */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[#1A1915]">Your Name</Label>
                      <Input value={shareForm.name} onChange={(e) => setShareForm(p => ({ ...p, name: e.target.value }))}
                        className="mt-1 bg-white border-[#D1CFC5]" placeholder="Optional"
                        data-testid="community-name" />
                    </div>
                    <div>
                      <Label className="text-[#1A1915]">Town</Label>
                      <Select value={shareForm.town} onValueChange={(v) => setShareForm(p => ({ ...p, town: v }))}>
                        <SelectTrigger className="mt-1 bg-white border-[#D1CFC5]" data-testid="community-town"><SelectValue placeholder="Select town" /></SelectTrigger>
                        <SelectContent>{towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button type="submit" disabled={submitting || uploading}
                    className="w-full bg-[#E87040] hover:bg-[#D45F30] text-white font-semibold py-6 rounded-xl text-lg"
                    data-testid="community-submit-btn">
                    {submitting ? "Submitting..." : "Share This Moment"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Blog;
